/**
 * Deterministic Learn Letters progress selectors.
 * Mirrors iOS AlphabetAMCourseProgress + per-letter MiniGame.allCases rules.
 * Completion requires every listed lesson ID (not array length alone).
 */

export const LETTERS = Array.from({ length: 26 }, (_, i) =>
  String.fromCharCode(65 + i),
);

/** Ordered lesson IDs per letter — must all exist for the letter to count complete. */
export const LETTER_LESSONS = {
  A: [
    "feedAlligator",
    "sentenceBuilder",
    "letterHunt",
    "appleTree",
    "pictureGrid",
    "popTheA",
    "traceA",
  ],
  B: [
    "storyPages",
    "bOrNotB",
    "letterHunt",
    "pictureMatch",
    "soundGarden",
    "wordBuilder",
    "traceB",
  ],
  C: [
    "archaeologyDig",
    "sentenceBuilder",
    "letterHunt",
    "pictureMatch",
    "soundSort",
    "caveEcho",
    "traceC",
  ],
  D: [
    "doorDiscovery",
    "soundSort",
    "letterHunt",
    "pictureMatch",
    "drumParade",
    "sentenceBuilder",
    "traceD",
  ],
  E: [
    "eggDiscovery",
    "shortESort",
    "letterHunt",
    "pictureMatch",
    "soundFerry",
    "wordWorkshop",
    "traceE",
  ],
  F: [
    "forestDiscovery",
    "soundSort",
    "fireflyHunt",
    "pictureMatch",
    "flowerGlow",
    "sentenceBuilder",
    "traceF",
  ],
  G: [
    "greenhouseDiscovery",
    "goatCallSequence",
    "letterHunt",
    "pictureMatch",
    "gardenMeaning",
    "sentenceBuilder",
    "traceG",
  ],
  H: [
    "hilltopDiscovery",
    "initialHSort",
    "letterHunt",
    "pictureMatch",
    "honeybeeHelp",
    "sentenceBuilder",
    "traceH",
  ],
  I: [
    "insectDiscovery",
    "shortISort",
    "letterHunt",
    "pictureMatch",
    "longIContrast",
    "wordWorkshop",
    "traceI",
  ],
  J: [
    "jewelDiscovery",
    "initialSoundSort",
    "letterHunt",
    "pictureMatch",
    "jungleJourney",
    "sentenceBuilder",
    "traceJ",
  ],
  K: [
    "keyGardenDiscovery",
    "initialSoundSort",
    "letterHunt",
    "pictureMatch",
    "kiteTrail",
    "sentenceBuilder",
    "traceK",
  ],
  L: [
    "lanternDiscovery",
    "initialSoundSort",
    "letterHunt",
    "pictureMatch",
    "ladybugTrail",
    "sentenceBuilder",
    "traceL",
  ],
  M: [
    "soundMixer",
    "initialSoundSort",
    "casePairing",
    "pictureMatch",
    "missingMBuilder",
    "sentenceBuilder",
    "traceM",
  ],
  N: [
    "soundSlide",
    "pictureMatch",
    "casePairing",
    "soundPosition",
    "soundPairs",
    "sentenceMeaning",
    "traceN",
  ],
  O: [
    "soundSlide",
    "casePairing",
    "soundPairs",
    "echoSequence",
    "missingOBuilder",
    "sentenceMeaning",
    "traceO",
  ],
  P: [
    "soundPuff",
    "soundPairs",
    "casePartners",
    "missingInitial",
    "sentenceMeaning",
    "traceP",
  ],
  Q: [
    "quTeamConnect",
    "casePartners",
    "soundPairDetective",
    "quCallSequence",
    "addTheTail",
    "missingQBuilder",
    "meaningGarden",
  ],
  R: [
    "soundRoad",
    "soundPairs",
    "casePartners",
    "wordRepair",
    "soundRoute",
    "sentenceMeaning",
    "buildR",
  ],
  S: [
    "soundSlider",
    "soundPairs",
    "casePartners",
    "soundSpots",
    "wordBuilder",
    "sentenceSense",
    "traceS",
  ],
  T: [
    "soundTrain",
    "soundPosition",
    "casePartners",
    "tapTrail",
    "wordBuilder",
    "sentenceMeaning",
    "buildT",
  ],
  U: [
    "umbrellaDiscovery",
    "shortUSoundPositions",
    "casePartners",
    "underTheArch",
    "missingUBuilder",
    "sentenceMeaning",
    "traceU",
  ],
  V: [
    "soundSlide",
    "casePartners",
    "soundPairDetective",
    "wordCallSequence",
    "buildV",
    "sentenceMeaning",
    "traceV",
  ],
  W: [
    "soundSlide",
    "casePartners",
    "pictureMemory",
    "soundPairs",
    "buildW",
    "missingWBuilder",
    "sentenceMeaning",
  ],
  X: [
    "soundSpotlight",
    "casePartners",
    "soundPosition",
    "picturePairs",
    "soundCall",
    "sentenceClues",
    "traceX",
  ],
  Y: [
    "soundSlide",
    "casePartners",
    "soundPairDetective",
    "callSequence",
    "twoJobs",
    "meaningGarden",
    "forkAndTail",
  ],
  Z: [
    "soundSlide",
    "casePartners",
    "soundPosition",
    "zigzagBuilder",
    "doubleZWorkshop",
    "sentenceMeaning",
    "traceZ",
  ],
};

export const D_STARTED_MARKER = "letter.d.started";

export const ALPHABET_WORLDS = [
  {
    id: "garden",
    title: "Garden World",
    letters: ["A", "B", "C", "D", "E"],
    startIndex: 0,
    overviewArt: "alphabet_world_garden",
    screenBackground: "garden_world_screen_background",
    palette: {
      cardTop: "#F0FFD9",
      cardBottom: "#DBF7BF",
      accent: "#2EA64F",
      ink: "#0F4F21",
    },
  },
  {
    id: "ocean",
    title: "Ocean World",
    letters: ["F", "G", "H", "I", "J"],
    startIndex: 5,
    overviewArt: "alphabet_world_ocean",
    screenBackground: "ocean_world_screen_background",
    palette: {
      cardTop: "#E8FAFF",
      cardBottom: "#CCF0FF",
      accent: "#0A8AD1",
      ink: "#083B82",
    },
  },
  {
    id: "sky",
    title: "Sky World",
    letters: ["K", "L", "M", "N", "O"],
    startIndex: 10,
    overviewArt: "alphabet_world_sky",
    screenBackground: "sky_world_screen_background",
    palette: {
      cardTop: "#EDFAFF",
      cardBottom: "#D1F0FF",
      accent: "#2E7AE0",
      ink: "#0A387D",
    },
  },
  {
    id: "star",
    title: "Star World",
    letters: ["P", "Q", "R", "S", "T"],
    startIndex: 15,
    overviewArt: "alphabet_world_star",
    screenBackground: "star_world_screen_background",
    palette: {
      cardTop: "#F5EBFF",
      cardBottom: "#E0CCFA",
      accent: "#754DC9",
      ink: "#381C6E",
    },
  },
  {
    id: "rainbow",
    title: "Rainbow World",
    letters: ["U", "V", "W", "X", "Y", "Z"],
    startIndex: 20,
    overviewArt: "alphabet_world_rainbow",
    screenBackground: "rainbow_world_screen_background",
    palette: {
      cardTop: "#FFF2F5",
      cardBottom: "#FFD6E0",
      accent: "#E34070",
      ink: "#751233",
    },
  },
];

export function courseKeyForLetter(letter) {
  return `letter.${String(letter).toLowerCase()}`;
}

export function completedIdsForLetter(courses, letter) {
  const raw = courses?.[courseKeyForLetter(letter)] || [];
  return new Set(Array.isArray(raw) ? raw : []);
}

/** True when every current lesson ID for the letter is present. */
export function letterLessonsComplete(courses, letter) {
  const upper = String(letter).toUpperCase();
  const required = LETTER_LESSONS[upper];
  if (!required) return false;
  const completed = completedIdsForLetter(courses, upper);
  return required.every((id) => completed.has(id));
}

/**
 * Whether this letter counts toward the contiguous prefix.
 * D also requires markers contains letter.d.started (iOS hasStarted && isComplete).
 */
export function letterCountsTowardPrefix(courses, letter) {
  const upper = String(letter).toUpperCase();
  if (!letterLessonsComplete(courses, upper)) return false;
  if (upper === "D") {
    const markers = new Set(courses?.markers || []);
    return markers.has(D_STARTED_MARKER);
  }
  return true;
}

/** Contiguous completed prefix length from A. */
export function completedLetterCount(courses = {}) {
  let count = 0;
  for (const letter of LETTERS) {
    if (!letterCountsTowardPrefix(courses, letter)) break;
    count += 1;
  }
  return count;
}

export function nextLetter(completedCount) {
  if (completedCount >= 0 && completedCount < LETTERS.length) {
    return LETTERS[completedCount];
  }
  return null;
}

export function isLetterUnlocked(letter, completedCount) {
  const index = LETTERS.indexOf(String(letter).toUpperCase());
  if (index < 0) return false;
  return index <= completedCount;
}

export function isWorldUnlocked(worldId, completedCount) {
  const world = ALPHABET_WORLDS.find((w) => w.id === worldId);
  if (!world) return false;
  return completedCount >= world.startIndex;
}

export function worldForCompletedCount(completedCount) {
  if (completedCount < 5) return ALPHABET_WORLDS[0];
  if (completedCount < 10) return ALPHABET_WORLDS[1];
  if (completedCount < 15) return ALPHABET_WORLDS[2];
  if (completedCount < 20) return ALPHABET_WORLDS[3];
  return ALPHABET_WORLDS[4];
}

export function worldContainingLetter(letter) {
  const upper = String(letter || "").toUpperCase();
  return (
    ALPHABET_WORLDS.find((w) => w.letters.includes(upper)) || ALPHABET_WORLDS[0]
  );
}

/** complete | current | locked for a letter chip/node. */
export function letterChipState(letter, completedCount) {
  const index = LETTERS.indexOf(String(letter).toUpperCase());
  if (index < 0) return "locked";
  if (index < completedCount) return "complete";
  if (index === completedCount && index < LETTERS.length) return "current";
  return "locked";
}

/** First unfinished lesson ID for a letter, or null if all done. */
export function nextLessonId(courses, letter) {
  const upper = String(letter).toUpperCase();
  const required = LETTER_LESSONS[upper];
  if (!required) return null;
  const completed = completedIdsForLetter(courses, upper);
  return required.find((id) => !completed.has(id)) || null;
}

export function lessonRoute(letter, gameId) {
  return `/learn/letters/${String(letter).toLowerCase()}/${gameId}`;
}

export function selectLearnLetterProgress(courses = {}) {
  const count = completedLetterCount(courses);
  const current = nextLetter(count);
  return {
    courses,
    completedLetterCount: count,
    nextLetter: current,
    currentWorld: current
      ? worldContainingLetter(current)
      : ALPHABET_WORLDS[ALPHABET_WORLDS.length - 1],
    lettersFinished: current == null,
    nextLessonId: current ? nextLessonId(courses, current) : null,
  };
}

/** Garden-local completed count (A–E prefix only), max 5. */
export function gardenCompletedCount(courses = {}) {
  return Math.min(5, completedLetterCount(courses));
}

export const GARDEN_NODES = [
  { letter: "A", x: 0.323, y: 0.803, unlockAt: 0, emphasize: true },
  { letter: "B", x: 0.628, y: 0.669, unlockAt: 1 },
  { letter: "C", x: 0.516, y: 0.558, unlockAt: 2 },
  { letter: "D", x: 0.73, y: 0.477, unlockAt: 3 },
  { letter: "E", x: 0.583, y: 0.387, unlockAt: 4 },
];

export const GARDEN_ASPECT = 853 / 1844;

export function gardenActiveLetter(gardenCount) {
  if (gardenCount >= 5) return null;
  return LETTERS[gardenCount];
}
